export const ENDPOINTS = {

  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    PROFILE: '/user/profile',
    KEYCLOAK_TOKEN: '/auth/cookies',
    KEYCLOAK_LOGOUT: '/auth/keycloak/logout',
    COMPANIES: '/firm',
    SELECT_COMPANY: '/identity/active-firm',
    CLEAR_COMPANY: '/identity/active-firm',
    SELECT_SITE: '/identity/active-site',
    CLEAR_SITE: '/identity/active-site',
    // Mints a single-use ticket for launching a product without a second login.
    LAUNCH_TICKET: '/auth/launch-ticket',
  },
  ACCOUNT: {
    SIGNUP: '/account/signup',
    CHECK_USERNAME: '/account/check-username',
    CHECK_EMAIL: '/account/check-email',
    CHECK_SUBDOMAIN: '/account/check-subdomain',
    GENERATE_SUBDOMAIN: '/account/generate-subdomain',
  },
  IDENTITY: {
    PROFILE: '/identity/me/profile',
    // USERS: '/identity/user',
    FIRM_GROUPS: '/identity/firm-groups',
    UPDATE_PROFILE: '/identity/me/profile',
    CHANGE_PASSWORD: '/identity/user/password/change', // NOSONAR S2068 — API route, not a credential
    VERIFY_PASSWORD_SETUP: '/identity/verify-password-setup', // NOSONAR S2068 — API route, not a credential
    UPDATE_SUBDOMAIN: '/identity/me/subdomain',
    MAIN_CONTACT_OPTIONS: '/identity/me/main-contact-options',
    GET_REGIONS: '/identity/halopsa-regions',
    GET_COUNTRIES: '/identity/halopsa-countries',
    NOTIFICATION_SETTINGS: '/identity/me/notification-settings',
    SSO_PROVIDERS: '/identity/me/sso/providers',
    SSO_CONFIGURATION: '/identity/me/sso/configuration',
    GET_REGIONS_BY_COUNTRY: (countryCode) => `/identity/halopsa-regions?countryCode=${countryCode}`,
    ROLES: '/identity/roles',
    USER_ROLES: '/identity/user-roles',
    DELETE_FIRM: (firmId) => `/identity/firm/${firmId}`,
    TRANSFER_OWNERSHIP: '/identity/transfer-ownership',
    AUTHENTICATOR: '/identity/me/authenticator',
  },

  FIRM: {
    UPDATE_MFA: '/firm/mfa',
  },

  TENANTS: {
    // Pre-auth subdomain resolver — anonymous. Returns the company + Keycloak IdP alias for a subdomain.
    RESOLVE: (subdomain) => `/tenants/${encodeURIComponent(subdomain)}`,
  },

  SIDEBAR: {
    VISIBILITY: '/sidebar/visibility',
  },

  SUBSCRIPTIONS: {
    LIST: '/subscriptions',
    CREATE: '/subscriptions',
    GET: (id) => `/subscriptions/${id}`,
    UPDATE: (id) => `/subscriptions/${id}`,
    CANCEL: (id) => `/subscriptions/${id}/cancel`,
  },

  BILLING: {
    INFO: '/billing',
    INVOICES: '/billing/invoices',
    PAYMENT_METHODS: '/billing/payment-methods',
    SETUP_INTENT: '/billing/payment-methods/setup-intent',
    VERIFY_ACH: '/billing/payment-methods/verify-ach',
    ACH_MANDATE: '/billing/payment-methods/ach/mandate',
    SET_DEFAULT: (token) => `/billing/payment-methods/${token}/default`,
    DELETE_METHOD: (token) => `/billing/payment-methods/${token}`,
    UPDATE_DETAILS: '/billing/billing-details',
    TAX_CONTEXT: '/billing/tax-context',
    GET_INVOICES: '/billing/invoices',
    GET_INVOICE_SUMMARY: '/billing/invoices/summary',
    GET_INVOICE_DETAIL: '/billing/invoices/detail',
    GET_INVOICE_RENEWAL_PRODUCTS: (id) => `/billing/invoices/${id}/renewal-products`,
    DOWNLOAD_INVOICE: (id) => `/billing/invoices/${id}/download`,
    EMAIL_INVOICE: (id) => `/billing/invoices/${id}/email`,
    EXPORT_INVOICES: '/billing/invoices/export',
    PREVIEW_INVOICE: (id) => `/billing/invoices/${id}/preview`,
    SET_INVOICE_PAYMENT_OPTION: (id) => `/billing/invoices/${id}/payment-option`,
    PROCESS_PAYMENT: (id) => `/billing/invoices/${id}/pay`,
    PAY_INVOICE: (id) => `/billing/invoices/${id}/pay`,
    VOID_INVOICE: (id) => `/billing/invoices/${id}/void`,
    AUTO_RENEWAL: '/billing/auto-renewal',
    AUTO_RENEWAL_SUGGESTION: '/billing/auto-renewal/suggestion',
  },

  TEAM: {
    LIST: '/team',
    INVITE: '/team/invite',
    REMOVE: (id) => `/team/${id}`,
    UPDATE_ROLE: (id) => `/team/${id}/role`,
  },

  ACTIVITY: {
    LIST: '/activity',
  },

  SUPPORT: {
    TICKETS: '/support/tickets',
    CREATE: '/support/tickets',
    GET: (id) => `/support/tickets/${id}`,
  },

  NEWSLETTER: {
    SUBSCRIBE: '/newsletter/subscribe',
    UNSUBSCRIBE: '/newsletter/unsubscribe',
    STATUS: '/newsletter/status',
  },

  DATANET: {
    ARTICLES: (clientId, search) => {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      return `/datanet/articles?clientId=${clientId}${searchParam}`;
    },
    DOWNLOAD: (articleId) => `/datanet/articles/${articleId}/download`,
    // Structured rate table for the edition's month/year, as parsed server-side from its HaloPSA
    // article name (see getArticles).
    RATES: (month, year) => `/datanet/rates?month=${encodeURIComponent(month)}&year=${year}`,
  },

  RESOURCES: {
    LIST: '/resources',
    DOWNLOADS: '/resources/downloads',
  },

  USER: {
    LIST: '/user/users',
    CREATE: '/user',
    GET_DETAILS: (id) => `/user/${id}`,
    UPDATE: (id) => `/user/${id}`,
    UPDATE_DATANET_EMAILS: (id) => `/user/${id}/datanet-emails`,
    CHECK_EMAIL: '/user/check-email',
    CHECK_USERNAME: '/user/check-username',
    SUBSCRIPTIONS: '/user/subscriptions',
    BATCH_LICENSES: '/user/licenses/batch',
    SEAT_INCREASE: '/user/licenses/seat-increase',
    SEAT_DECREASE: '/user/licenses/seat-decrease',
    REMOVE_AUTHENTICATOR: (id) => `/user/${id}/authenticator`,
    RESEND_INVITE: (id) => `/user/${id}/resend-invite`,
    // Per-firm activate / deactivate. Separate action routes rather than a field on UPDATE: they
    // release the user's licence seats and can end their session, which a profile edit must not do.
    DEACTIVATE: (id) => `/user/${id}/deactivate`,
    REACTIVATE: (id) => `/user/${id}/reactivate`,
  },

  // Bulk user import (US-SP07-USR-001)
  USER_IMPORT: {
    SAMPLE: (format = 'xlsx') => `/user-import/sample?format=${format}`,
    PREVIEW: '/user-import/preview',
    START: (jobId) => `/user-import/${jobId}/start`,
    STATUS: (jobId) => `/user-import/${jobId}`,
    ROWS: (jobId, page = 1, pageSize = 25) =>
      `/user-import/${jobId}/rows?page=${page}&pageSize=${pageSize}`,
    UNPROCESSED: (jobId, format = 'xlsx') =>
      `/user-import/${jobId}/unprocessed?format=${format}`,
  },

  PRODUCTS: {
    LIST: '/products',
    DOWNLOADS: '/products/downloads',
  },

  SUBSCRIPTION: {
    CREATE: '/subscriptions/purchase',
    PURCHASE_BATCH: '/subscriptions/purchase-batch',
    CHECKOUT: '/subscriptions/checkout',
    LIST: '/subscriptions/list',
    PRODUCTS: '/subscriptions/available-products',
    PURCHASABLE_PRODUCTS: '/subscriptions/purchasable-products',
    ADD_PRODUCT: '/subscriptions/add-product',
    UPDATE_SEATS: (id) => `/subscriptions/${id}/seats`,
    RENEWAL_CONTEXT: (productClientId) => `/subscriptions/renewal-context/${productClientId}`,
    RENEWAL_PREVIEW: (productClientId, targetSeats) =>
      `/subscriptions/renewal-preview/${productClientId}?targetSeats=${targetSeats}`,
    RENEWAL_SEATS: '/subscriptions/renewal-seats',
    UPDATE_BUNDLE_NAME: '/subscriptions/bundles/name',
    REQUEST_SEAT_CHANGE: '/subscriptions/request-seat-change',
  },

  DOWNLOAD: {
    PRODUCTS: '/download/products',
    PRODUCT_DOWNLOADS: (productClientId) => `/download/product-downloads/${productClientId}`,
    RESOURCES_BY_PRODUCT: (productClientId) => `/download/download-resources/product/${productClientId}`,
    RESOURCE_DETAILS: (resourceId) => `/download/download-resources/${resourceId}`,
  },

  DASHBOARD: '/dashboard',

  QUOTATIONS: {
    // Read paths — still active.
    LIST: '/quotations',
    GET_DETAIL: (id) => `/quotations/${id}`,
    GET_PREVIEW: (id) => `/quotations/${id}/preview`,
    DOWNLOAD: (id) => `/quotations/${id}/download`,
    // Quotation Request ticket + Accept/Decline (plan F1).
    // Route is /ticket-request (not /request) so the URL reflects that the outbound Halo call
    // hits /api/Tickets, not /api/Quotation.
    REQUEST: '/quotations/ticket-request',
    ACCEPT: (id) => `/quotations/${id}/accept`,
    DECLINE: (id) => `/quotations/${id}/decline`,
  },
};
